<template>
  <DsPageContent>
    <DsSectionImage v-if="content.headingMedia || content.headingVideo">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsScreenWrapper
            v-if="content.headingVideo"
            :ready="hydrateReady"
          >
            <DsVideoPlayer
              v-if="content.headingVideo"
              :aria-label-play="labels.play"
              :aria-label-close="labels.close"
              :screen="screen"
              v-bind="content.headingVideo"
              class="ds-width-full"
            />
          </DsScreenWrapper>
          <DsScreenWrapper v-else-if="content.headingMedia">
            <DsImageElement
              v-if="content.headingMedia"
              :image="content.headingMedia"
              :screen="screen"
            />
          </DsScreenWrapper>
        </div>
      </div>
    </DsSectionImage>

    <DsSection v-if="content">
      <div class="ds-grid ds-justify-content-center">
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-text-align-center">
          <span
            class="ds-text-medium"
            v-text="content.city?.countryName"
          />
          <h1
            class="ds-margin-y-24 ds-heading-xlarge"
            v-text="content.city?.name"
          />
          <p
            class="ds-text-medium"
            v-text="content.description"
          />

          <DsDivider
            v-if="content.locationFacts"
            class="ds-padding-top-48 ds-md-padding-top-96"
          />

          <DsGrid
            v-if="content.locationFacts"
            :items="content.locationFacts"
            :column-width="{ small: 12, medium: 6, large: 6 }"
            class="ds-padding-top-48 usps"
          >
            <template #default="{ item }">
              <DsCardTextIcon
                :icon="item.icon"
                :title="item.title"
              >
                <RichText :data="item.description" />
              </DsCardTextIcon>
            </template>
          </DsGrid>
        </div>
      </div>
    </DsSection>

    <DsSection
      :with-space-bottom="false"
      theme="grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsMediaTextSection
            v-if="techHub && techHub.image"
            class="ds-background-color-white"
            :title="techHub.title"
            :image="techHub.image"
            text=""
            :link="techHub.link"
          >
            <template #text>
              <RichText
                v-if="techHub.description"
                :data="techHub.description"
                class="ds-margin-top-12"
              />
            </template>
          </DsMediaTextSection>
        </div>
      </div>
    </DsSection>

    <template v-if="testimonials.length > 0">
      <DsSection
        theme="grey"
        full-screen
      >
        <ClientOnly>
          <DsImageGallery
            :screen="screen"
            :items="testimonials.length"
            :title="content.testimonials.title"
            :description="content.testimonials.description"
            :width="{ small: 272, large: 368 }"
          >
            <template #default="{ index }">
              <DsCard
                :image="testimonials[index - 1].image"
                :title="testimonials[index - 1].author || testimonials[index - 1].title"
                :video="appendLabelsToVideo(testimonials[index - 1].video)"
                :subtitle="testimonials[index - 1].role"
                :text="testimonials[index - 1].quote || testimonials[index - 1].description"
                :link="testimonials[index - 1].link"
                aspect-ratio="16/9"
                with-subtitle-size-large
              />
            </template>
          </DsImageGallery>
        </ClientOnly>
      </DsSection>

      <DsSection
        theme="grey"
        :with-space="false"
      >
        <div class="ds-grid">
          <div class="ds-grid__col">
            <DsDivider />
          </div>
        </div>
      </DsSection>
    </template>

    <DsSection
      v-if="content.perks"
      theme="grey"
    >
      <div class="ds-grid ds-justify-content-center">
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-text-align-center">
          <h2
            class="ds-heading ds-margin-bottom-48"
            v-text="content.perksTitle"
          />
          <DsGrid
            :items="content.perks"
            :column-width="{ small: 6, medium: 3, large: 3 }"
            class="ds-justify-content-center"
          >
            <template #default="{ item }">
              <DsCardTextIcon
                :icon="item.icon"
                title=""
                alignment="center"
              >
                <RichText
                  :data="item.description"
                  class="ds-font-weight-semi-bold"
                />
              </DsCardTextIcon>
            </template>
          </DsGrid>
        </div>
      </div>

      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsMediaTextSection
            v-if="content.office && content.office.image"
            class="ds-background-color-white ds-margin-top-48 ds-md-margin-top-96"
            :title="content.office.name"
            :image="content.office.image"
            text=""
            eyebrow=""
            :link="officeLink"
          >
            <template #eyebrow>
              <DsIcon
                v-if="cityIcon"
                :name="cityIcon.name"
                :category="cityIcon.category"
              />
            </template>

            <template #text>
              <div>
                <p v-if="office.addressLine1">
                  {{ office.addressLine1 }}
                </p>
                <p v-if="office.addressLine2">
                  {{ office.addressLine2 }}
                </p>
                <p v-if="office.phoneNumber">
                  {{ office.phoneNumber }}
                </p>
              </div>
            </template>
          </DsMediaTextSection>
        </div>
      </div>
    </DsSection>

    <DsSection v-if="content">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <CareersVacanciesList
            :office="content.city?.uniqueId"
            as-widget
          />
        </div>
      </div>
    </DsSection>
    <DsSection
      v-if="content.disclaimer"
      theme="grey"
    >
      <RichText :data="content.disclaimer" />
    </DsSection>
  </DsPageContent>
</template>

<script setup>
import {
  DsIcon,
  DsSection,
  DsSectionImage,
  DsImageGallery,
  DsCard,
  DsGrid,
  DsCardTextIcon,
  DsScreenWrapper,
  DsVideoPlayer,
  DsPageContent,
  DsMediaTextSection,
  DsDivider,
  DsImageElement
} from 'awds'
import { queryPageCareersLocationsDetail } from '../../data-layer/queries'

const { screen } = useScreen()
const { useAsyncDataWithQuery, onBeforeAdyenData, onAfterAdyenData, content } = useCareersPageData()
const { labels } = useCareersGlobalData()
const { getIconForCity } = useGreenHouseUtils()
const { hydrateReady } = useHydrateHook()
const route = useRoute()
const { locale } = useI18n()

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PageCareersLocation' })

const { data } = await useAsyncData('careers-loc-' + route.params.slug + '-' + locale.value, async () => {
  const page = await useAsyncDataWithQuery(queryPageCareersLocationsDetail, {})

  return {
    pageData: page?.pageData
  }
})

await onAfterAdyenData(data.value)

// Computed
const testimonials = computed(() => {
  return content.value.testimonials?.items || []
})

const techHub = computed(() => {
  return content?.value.techHub || null
})

const office = computed(() => {
  return content.value?.office || {}
})

const officeLink = computed(() => {
  if (!office.value) {
    return null
  }
  return {
    url: office.value.addressLink,
    openInNewWindow: true,
    title: labels.value.directions || 'Directions',
    iconRight: { category: 'arrows', name: 'chevron-right-small' }
  }
})

const cityIcon = computed(() => {
  if (!content.value.city?.uniqueId) {
    return null
  }

  return getIconForCity(content.value.city?.uniqueId)
})

const appendLabelsToVideo = (video) => {
  if (!video) {
    return null
  }

  return {
    ariaLabelPlay: labels.value?.play,
    ariaLabelClose: labels.value?.close,
    ...video
  }
}
</script>
